import React, { useState } from 'react';

interface LinhasDRE {
  nivel: number;
  descricao: string;
  nivel1: string;
  nivel4: string | null;
  realizado: number;
  projetado: number;
  variacao_pct: number | null;
  analise_vertical: number | null;
}

interface Props {
  linhas: LinhasDRE[];
}

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const fmtPct = (v: number | null) => (v !== null ? `${(v * 100).toFixed(1)}%` : '—');

export function TabelaDRE({ linhas }: Props) {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set(['1']));

  const toggle = (key: string) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const visiveis = linhas.filter((l) => {
    if (l.nivel === 1) return true;
    if (l.nivel === 2) return expandidos.has(l.nivel1);
    if (l.nivel === 3) return expandidos.has(l.nivel1) && expandidos.has(`${l.nivel1}-${(l as any).nivel2}`);
    if (l.nivel === 4) {
      const n2 = (l as any).nivel2;
      const n3 = (l as any).nivel3;
      return expandidos.has(l.nivel1) && expandidos.has(`${l.nivel1}-${n2}`) && expandidos.has(`${l.nivel1}-${n2}-${n3}`);
    }
    return false;
  });

  return (
    <div style={styles.wrapper}>
      <h3 style={styles.titulo}>DRE Gerencial</h3>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, textAlign: 'left' }}>Conta</th>
            <th style={styles.th}>Realizado</th>
            <th style={styles.th}>Projetado</th>
            <th style={styles.th}>Var %</th>
            <th style={styles.th}>AV %</th>
          </tr>
        </thead>
        <tbody>
          {visiveis.map((l, i) => {
            const indent = (l.nivel - 1) * 16;
            const isExpandivel = l.nivel < 4;
            const key = l.nivel === 1
              ? l.nivel1
              : l.nivel === 2
              ? `${l.nivel1}-${(l as any).nivel2}`
              : l.nivel === 3
              ? `${l.nivel1}-${(l as any).nivel2}-${(l as any).nivel3}`
              : null;

            const cor = l.variacao_pct !== null
              ? l.variacao_pct >= 0 ? '#48d951' : '#E24B4A'
              : '#94a3b8';

            return (
              <tr key={i} style={{ background: i % 2 === 0 ? '#1a1f2e' : '#1e2130' }}>
                <td style={{ ...styles.td, paddingLeft: 12 + indent }}>
                  {isExpandivel && key && (
                    <span
                      onClick={() => toggle(key)}
                      style={styles.toggle}
                    >
                      {expandidos.has(key) ? '▾' : '▸'}
                    </span>
                  )}
                  <span style={{ fontWeight: l.nivel <= 2 ? 600 : 400, fontSize: l.nivel === 1 ? 13 : 12 }}>
                    {l.descricao}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(l.realizado)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#64748b' }}>{fmt(l.projetado)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: cor }}>
                  {fmtPct(l.variacao_pct)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#64748b' }}>
                  {fmtPct(l.analise_vertical)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    background: '#1e2130',
    borderRadius: 12,
    padding: 24,
    color: '#e2e8f0',
    fontFamily: 'Inter, system-ui, sans-serif',
    overflowX: 'auto',
  },
  titulo: { margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#94a3b8' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    padding: '8px 12px',
    color: '#64748b',
    fontWeight: 500,
    borderBottom: '1px solid #2d3347',
    textAlign: 'right',
  },
  td: { padding: '7px 12px', borderBottom: '1px solid #252a3d', color: '#e2e8f0' },
  toggle: { cursor: 'pointer', marginRight: 6, color: '#64748b', userSelect: 'none' },
};
